import React, { useState, useEffect } from 'react';
import {
  Plus, Trash2, Edit3, Save, X, Loader2, ExternalLink, Github,
  Star, Eye, EyeOff, Search
} from 'lucide-react';
import { useApi } from '../AdminApp';

const ProjectsEditor = () => {
  const { apiCall } = useApi();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [message, setMessage] = useState({ type: '', text: '' });

  const emptyProject = {
    title: { tr: '', en: '', de: '' },
    description: { tr: '', en: '', de: '' },
    technologies: [],
    github: '',
    live: '',
    image: '',
    featured: false,
    status: 'active',
    order: 0
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await apiCall('/projects');
      setProjects(data);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const openModal = (project = null) => {
    setEditingProject(project ? { ...project } : { ...emptyProject, order: projects.length });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setEditingProject(null);
    setIsModalOpen(false);
  };

  const handleSave = async () => {
    if (!editingProject.title.en.trim()) {
      setMessage({ type: 'error', text: 'Project title is required' });
      return;
    }

    try {
      if (editingProject.id) {
        await apiCall(`/projects/${editingProject.id}`, {
          method: 'PUT',
          body: JSON.stringify(editingProject)
        });
      } else {
        await apiCall('/projects', {
          method: 'POST',
          body: JSON.stringify(editingProject)
        });
      }
      fetchProjects();
      closeModal();
      setMessage({ type: 'success', text: 'Project saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await apiCall(`/projects/${id}`, { method: 'DELETE' });
      fetchProjects();
      setMessage({ type: 'success', text: 'Project deleted successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const toggleFeatured = async (project) => {
    try {
      await apiCall(`/projects/${project.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...project, featured: !project.featured })
      });
      fetchProjects();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const toggleStatus = async (project) => {
    const newStatus = project.status === 'active' ? 'draft' : 'active';
    try {
      await apiCall(`/projects/${project.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...project, status: newStatus })
      });
      fetchProjects();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.title.en.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.title.tr.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || p.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white">Projects</h1>
          <p className="text-gray-400 mt-1">Manage your portfolio projects</p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition"
        >
          <Plus className="w-5 h-5" />
          Add Project
        </button>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 rounded-xl ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/50 text-green-400' : 'bg-red-500/10 border border-red-500/50 text-red-400'}`}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search projects..."
            className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredProjects.map((project) => (
          <div
            key={project.id}
            className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden group hover:border-blue-500/50 transition-all duration-300"
          >
            {/* Project Image */}
            <div className="relative h-40 bg-gray-700/50">
              {project.image ? (
                <img
                  src={project.image}
                  alt={project.title.en}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  No Image
                </div>
              )}
              {/* Status Badge */}
              <div className="absolute top-3 left-3 flex gap-2">
                {project.featured && (
                  <span className="px-2 py-1 bg-yellow-500/90 text-black text-xs font-bold rounded-lg flex items-center gap-1">
                    <Star className="w-3 h-3" fill="currentColor" /> Featured
                  </span>
                )}
                <span className={`px-2 py-1 text-xs font-bold rounded-lg ${
                  project.status === 'active' ? 'bg-green-500/90 text-white' :
                  project.status === 'draft' ? 'bg-gray-500/90 text-white' :
                  'bg-red-500/90 text-white'
                }`}>
                  {project.status}
                </span>
              </div>
            </div>

            {/* Project Info */}
            <div className="p-4">
              <h3 className="text-lg font-semibold text-white truncate">{project.title.en || project.title.tr}</h3>
              <p className="text-gray-400 text-sm mt-1 line-clamp-2">{project.description.en || project.description.tr}</p>

              {/* Technologies */}
              <div className="flex flex-wrap gap-2 mt-3">
                {project.technologies.slice(0, 3).map((tech, i) => (
                  <span key={i} className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-lg">
                    {tech}
                  </span>
                ))}
                {project.technologies.length > 3 && (
                  <span className="px-2 py-1 bg-gray-700 text-gray-400 text-xs rounded-lg">
                    +{project.technologies.length - 3}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700/50">
                <div className="flex gap-2">
                  {project.github && (
                    <a href={project.github} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition">
                      <Github className="w-5 h-5" />
                    </a>
                  )}
                  {project.live && (
                    <a href={project.live} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition">
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => toggleFeatured(project)}
                    className={`p-2 rounded-lg transition ${project.featured ? 'text-yellow-400 bg-yellow-500/20' : 'text-gray-400 hover:text-yellow-400 hover:bg-gray-700'}`}
                    title="Toggle Featured"
                  >
                    <Star className="w-5 h-5" fill={project.featured ? 'currentColor' : 'none'} />
                  </button>
                  <button
                    onClick={() => toggleStatus(project)}
                    className={`p-2 rounded-lg transition ${project.status === 'active' ? 'text-green-400' : 'text-gray-400'} hover:bg-gray-700`}
                    title="Toggle Visibility"
                  >
                    {project.status === 'active' ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => openModal(project)}
                    className="p-2 text-blue-400 hover:bg-blue-500/20 rounded-lg transition"
                    title="Edit"
                  >
                    <Edit3 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(project.id)}
                    className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400">No projects found. Add your first project!</p>
        </div>
      )}

      {/* Edit Modal */}
      {isModalOpen && <ProjectModal project={editingProject} setProject={setEditingProject} onSave={handleSave} onClose={closeModal} />}
    </div>
  );
};

const ProjectModal = ({ project, setProject, onSave, onClose }) => {
  const [activeTab, setActiveTab] = useState('tr');
  const [techInput, setTechInput] = useState('');
  const [saving, setSaving] = useState(false);

  const tabs = [
    { id: 'tr', label: 'Türkçe', flag: '🇹🇷' },
    { id: 'en', label: 'English', flag: '🇺🇸' },
    { id: 'de', label: 'Deutsch', flag: '🇩🇪' }
  ];

  const handleChange = (field, value, lang = null) => {
    if (lang) {
      setProject(prev => ({
        ...prev,
        [field]: { ...prev[field], [lang]: value }
      }));
    } else {
      setProject(prev => ({ ...prev, [field]: value }));
    }
  };

  const addTech = () => {
    if (!techInput.trim()) return;
    setProject(prev => ({
      ...prev,
      technologies: [...prev.technologies, techInput.trim()]
    }));
    setTechInput('');
  };

  const removeTech = (index) => {
    setProject(prev => ({
      ...prev,
      technologies: prev.technologies.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave();
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-gray-900 rounded-2xl border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">
            {project.id ? 'Edit Project' : 'New Project'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-xl transition">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Language Tabs */}
          <div className="flex border-b border-gray-700">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium transition ${
                  activeTab === tab.id
                    ? 'text-white border-b-2 border-blue-500'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <span>{tab.flag}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Title ({activeTab.toUpperCase()}) *
            </label>
            <input
              type="text"
              value={project.title[activeTab] || ''}
              onChange={(e) => handleChange('title', e.target.value, activeTab)}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              placeholder="Project title..."
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Description ({activeTab.toUpperCase()})
            </label>
            <textarea
              value={project.description[activeTab] || ''}
              onChange={(e) => handleChange('description', e.target.value, activeTab)}
              rows={4}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition resize-none"
              placeholder="Project description..."
            />
          </div>

          {/* Technologies */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Technologies</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={techInput}
                onChange={(e) => setTechInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTech())}
                className="flex-1 px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                placeholder="Add technology..."
              />
              <button
                onClick={addTech}
                className="px-4 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {project.technologies.map((tech, i) => (
                <span key={i} className="flex items-center gap-1 px-3 py-1 bg-blue-500/20 text-blue-400 rounded-lg">
                  {tech}
                  <button onClick={() => removeTech(i)} className="hover:text-red-400">
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Links */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">GitHub URL</label>
              <input
                type="url"
                value={project.github || ''}
                onChange={(e) => handleChange('github', e.target.value)}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                placeholder="https://github.com/..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Live URL</label>
              <input
                type="url"
                value={project.live || ''}
                onChange={(e) => handleChange('live', e.target.value)}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                placeholder="https://..."
              />
            </div>
          </div>

          {/* Image */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Image URL</label>
            <input
              type="url"
              value={project.image || ''}
              onChange={(e) => handleChange('image', e.target.value)}
              className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              placeholder="https://..."
            />
          </div>

          {/* Options */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={project.featured}
                onChange={(e) => handleChange('featured', e.target.checked)}
                className="w-5 h-5 rounded border-gray-600 text-blue-500 focus:ring-blue-500 bg-gray-700"
              />
              <span className="text-gray-300">Featured Project</span>
            </label>
            <div className="flex items-center gap-2">
              <label className="text-gray-300">Status:</label>
              <select
                value={project.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="px-3 py-2 bg-gray-800/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-6 py-3 text-gray-300 hover:text-white hover:bg-gray-700 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            {saving ? 'Saving...' : 'Save Project'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectsEditor;
